import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Avatar,
  Box,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountCircleOutlined,
  EditOutlined,
  CardMembershipOutlined,
  Palette,
  Gavel,
} from '@mui/icons-material';
import { useAuthStore } from '../stores/useAuthStore';

const ProfileMenu: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuthStore();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (path: string) => {
    navigate(path);
    handleClose();
  };

  // Obtenir les initiales de l'utilisateur
  const getInitials = () => {
    if (!user) return '?';
    const prenom = user.prenom || '';
    const nom = user.nom || '';
    return `${prenom.charAt(0)}${nom.charAt(0)}`.toUpperCase();
  };

  return (
    <>
      <IconButton
        onClick={handleClick}
        sx={{
          p: 0.5,
          '&:hover': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: theme.palette.secondary.main,
            fontSize: '0.95rem',
            fontWeight: 600,
            border: open ? `2px solid ${theme.palette.secondary.light}` : 'none',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
            cursor: 'pointer',
            '&:hover': {
              transform: 'scale(1.1) rotate(-3deg)',
              boxShadow: `0 4px 12px ${theme.palette.secondary.main}40`,
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
          }}
        >
          {getInitials()}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        TransitionProps={{
          timeout: 350,
        }}
        PaperProps={{
          elevation: 8,
          sx: {
            mt: 1.5,
            minWidth: { xs: 240, sm: 260 },
            maxWidth: 260,
            borderRadius: 2,
            overflow: 'visible',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            animation: 'slideIn 0.25s ease-out',
            '@keyframes slideIn': {
              '0%': {
                opacity: 0,
                transform: 'translateY(-8px) scale(0.96)',
              },
              '100%': {
                opacity: 1,
                transform: 'translateY(0) scale(1)',
              },
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
      >
        {/* Header du menu avec info utilisateur */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: theme.palette.secondary.main,
                fontSize: '0.95rem',
                fontWeight: 600,
              }}
            >
              {getInitials()}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: '#1f2937',
                  lineHeight: 1.3,
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.prenom} {user?.nom}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#6b7280',
                  fontSize: '0.7rem',
                  display: 'block',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {user?.email}
              </Typography>
              {user?.subscriptionActive && (
                <Box
                  sx={{
                    display: 'inline-block',
                    mt: 0.5,
                    px: 0.75,
                    py: 0.125,
                    borderRadius: 0.75,
                    bgcolor: '#dcfce7',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: '#065f46',
                  }}
                >
                  {user.subscriptionTier === 'PREMIUM' ? 'Premium' : 'Essentiel'}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Menu Items */}
        <Box sx={{ py: 0.5 }}>
          <MenuItem
            onClick={() => handleMenuItemClick('/profile')}
            sx={{
              px: 2,
              py: 1.25,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#f9fafb',
                transform: 'translateX(3px)',
                '& .MuiListItemIcon-root': {
                  transform: 'scale(1.1)',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, transition: 'transform 0.2s ease' }}>
              <EditOutlined sx={{ color: '#6b7280', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Éditer mon profil"
              primaryTypographyProps={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#374151',
              }}
            />
          </MenuItem>

          <Divider sx={{ my: 0.5, mx: 1.5 }} />

          <MenuItem
            onClick={() => handleMenuItemClick('/customization')}
            sx={{
              px: 2,
              py: 1.25,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#faf5ff',
                transform: 'translateX(3px)',
                '& .MuiListItemIcon-root': {
                  transform: 'scale(1.1) rotate(-5deg)',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, transition: 'transform 0.2s ease' }}>
              <Palette sx={{ color: '#9333ea', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Personnaliser mes documents"
              primaryTypographyProps={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#374151',
              }}
            />
          </MenuItem>

          <MenuItem
            onClick={() => handleMenuItemClick('/legal-info')}
            sx={{
              px: 2,
              py: 1.25,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#fffbeb',
                transform: 'translateX(3px)',
                '& .MuiListItemIcon-root': {
                  transform: 'scale(1.1)',
                },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, transition: 'transform 0.2s ease' }}>
              <Gavel sx={{ color: '#f59e0b', fontSize: 20 }} />
            </ListItemIcon>
            <ListItemText
              primary="Informations légales"
              primaryTypographyProps={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: '#374151',
              }}
            />
          </MenuItem>

          {user?.subscriptionActive && (
            <>
              <Divider sx={{ my: 0.5, mx: 1.5 }} />
              <MenuItem
                onClick={() => handleMenuItemClick('/subscription')}
                sx={{
                  px: 2,
                  py: 1.25,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#f0fdf4',
                    transform: 'translateX(3px)',
                    '& .MuiListItemIcon-root': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, transition: 'transform 0.2s ease' }}>
                  <CardMembershipOutlined sx={{ color: '#10b981', fontSize: 20 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Mon abonnement"
                  primaryTypographyProps={{
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: '#374151',
                  }}
                />
              </MenuItem>
            </>
          )}
        </Box>
      </Menu>
    </>
  );
};

export default ProfileMenu;
